import EditIcon from "@mui/icons-material/Edit";
import { SortingState } from "@tanstack/react-table";
import ActionButton from "Common/Components/Buttons/ActionButton";
import ExportButton from "Common/Components/Buttons/ExportButton";
import ColumnVisibilityDropdown from "Common/Components/ColumnFilteringPopUp/ColumnFilteringPopUp";
import { DynamicMenuItem } from "Common/Components/DynamicActionMenu/DynamicActionMenu";
import LoaderErrorWrapper from "Common/Components/LoaderErrorWrapper/LoaderErrorWrapper";
import NameWithDate from "Common/Components/NameWithDate.tsx/NameWithDate";
import ListingPageContainer from "Common/Components/PageContainer/ListingPageContainer";
import SearchBox from "Common/Components/SearchBox/SearchBox";
import MasterTableContainer from "Common/Tables/MasterFiltersTable/MasterTableContainer";
import { app_modules } from "helpers/apps_modules_list";
import usePermission from "hooks/usePermission";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import { useEffect, useMemo, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { createSelector } from "reselect";
import {
  exportMakeCSV,
  fetchMakes as OnfetchMakes,
  updateStatusInMake as onUpdateStatusInMake,
} from "slices/Masters/make/thunk";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import { TableKeys } from "Constants/TableKeys/TableKeys";

const Make = () => {
  document.title = "Make List";
  const dispatch = useDispatch<any>();

  const { canView, canCreate, canUpdate, canDelete } = usePermission(
    app_modules?.master?.app_name,
    app_modules?.master?.modules?.make
  );

  // const [pageIndex, setPageIndex] = useState(0);
  // const [pageSize, setPageSize] = useState(10);
  const [data, setData] = useState<any[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [tableInstance, setTableInstance] = useState<any>();
  // State for edit functionality
  const [editMake, setEditMake] = useState<boolean>(false);
  // const [columnFilters, setColumnFilters] = useState<any>();
  const [edit, setEdit] = useState<any>();
  // const [sorting, setSorting] = useState<SortingState>([]);
  // const [searchInput, setSearchInput] = useState("");
  // const [search, setSearch] = useState("");
  // const [filterValue, setFilterValue] = useState<any[]>([]);

  const tableState = usePreservedTableState({
    key: TableKeys.MASTER.MAKE,
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue,
    resetState,
  } = tableState;

  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  useEffect(() => {
    dispatch(
      OnfetchMakes({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: filterValue,
      })
    );
  }, [dispatch, pageIndex, pageSize, sorting, search, filterValue]);

  const makeSelector = createSelector(
    (state: any) => state.Make,
    (make) => ({
      makesList: make.makeList,
      loading: make.loading,
      error: make.error,
      rowCount: make.rowCount,
    })
  );

  const { makesList, loading, error, rowCount } = useSelector(makeSelector);

  useEffect(() => {
    setData(makesList || []); // Set dummy data for rendering
    setTotalRecords(rowCount);
  }, [makesList, rowCount]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput)
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  // Handle edit action
  const handleStatusToggle = async (pkg: any) => {
    try {
      const updatedStatus = !pkg.is_active;
      const updatedMake = {
        make_id: pkg.make_id,
        is_active: updatedStatus,
      };
      await dispatch(onUpdateStatusInMake(updatedMake))
        .unwrap()
        .then(() => {
          toast.success(
            `Make ${updatedStatus ? "activated" : "deactivated"} successfully`
          );
        });
      dispatch(
        OnfetchMakes({
          page: pageIndex + 1,
          pageSize,
          sort: sortField,
          order: sortField ? sortDirection : "",
          search,
          filters: filterValue,
        })
      );
    } catch (err: any) {
      toast.error("Failed to Update Status");
    }
  };

  const columns =  [
      {
        header: "Sr. No.",
        accessorKey: "sr_no",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Make",
        accessorKey: "make_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.MASTERS?.MAKE?.FILTER_DROPDOWN(
          "make_name"
        )}`,
        cell: ({ row }: { row: any }) => (
          <div
            style={{
              whiteSpace: "normal",
              wordWrap: "break-word",
              width: "100px",
            }}
          >
            {row.original.make_name || "-"}
          </div>
        ),
      },
      {
        header: "Status",
        accessorKey: "is_active",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const pkg = row.original;
          return (
            <div
              style={{
                fontSize: "1.5rem",
                cursor: canUpdate ? "pointer" : "not-allowed",
              }}
              onClick={() => canUpdate && handleStatusToggle(pkg)}
            >
              <i
                className={`bi ${
                  pkg.is_active
                    ? "bi-toggle-on text-success"
                    : "bi-toggle-off text-danger"
                }`}
              />
            </div>
          );
        },
      },
      {
        header: "Created By/Date",
        accessorKey: "created_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        filterFetchUrl: API_ENDPOINT.MASTERS.MAKE.FILTER_DROPDOWN("created_at"),
        cell: ({ row }: { row: any }) => (
          <NameWithDate
            name={row.original?.created_by_user?.username}
            date={row.original.created_at}
          />
        ),
      },
      {
        header: "Updated By/Date",
        accessorKey: "updated_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        filterFetchUrl: API_ENDPOINT.MASTERS.MAKE.FILTER_DROPDOWN("updated_at"),
        cell: ({ row }: { row: any }) => (
          <NameWithDate
            name={row.original?.updated_by_user?.username}
            date={row.original.updated_at}
          />
        ),
      },
      // Filter menu items to check if any will be visible
      // This ensures we only show the Action column if there are visible menu items
      ...(canUpdate
        ? [
            {
              header: "Action",
              enableColumnFilter: false,
              enableSorting: false,
              cell: ({ row }: { row: any }) => {
                const menuItems = [
                  {
                    icon: EditIcon,
                    label: "Edit",
                    permission: canUpdate,
                    link: `/master/edit-make/${row.original.make_id}`,
                    color: "primary",
                  },
                ].filter((item) => item.permission === true);
                return (
                  <DynamicMenuItem row={row.original} menuItems={menuItems} />
                );
              },
            },
          ]
        : []),
    ]

  return (
    <ListingPageContainer>
      <Row id="invoiceList">
        <Col lg={12}>
          <Card className="shadow-none">
            <Card.Header>
              <Row className="align-items-center g-2">
                <Col lg={3} className="me-auto">
                  <h6 className="card-title mb-0">Make</h6>
                </Col>
                <Row className="align-items-center justify-content-between mt-1 px-0 g-3 flex-wrap">
                  {/* Search Box */}
                  <SearchBox
                    value={searchInput}
                    onChange={(e: any) => setSearchInput(e.target.value)}
                    placeholder="Search..."
                  />

                  {/* Column Visibility + Actions */}
                  <Col xs={12} md={12} lg={8} xl={6} className="">
                    <div className="d-flex flex-wrap align-items-center justify-content-end gap-2 w-100">
                      {/* Left side: Column Visibility */}
                      <div className="">
                        {tableInstance && (
                          <ColumnVisibilityDropdown
                            columns={tableInstance.getAllLeafColumns()}
                            excludedColumns={["action", "sr_no"]}
                          />
                        )}
                      </div>

                      <div className="hstack gap-2">
                        {canView && (
                        <ExportButton
                          onExport={async () => {
                            return await exportMakeCSV({
                              search,
                              filters: filterValue,
                            });
                          }}
                        />
                        )}
                      </div>

                      <div>
                        {canCreate && (
                        <ActionButton
                          label="Add Make"
                          icon="bi bi-plus-circle"
                          navigateLink="/master/add-make"
                          variant="primary"
                        />
                        )}
                      </div>
                    </div>
                  </Col>
                </Row>
              </Row>
            </Card.Header>
            <Card.Body className="mt-3">
              <LoaderErrorWrapper loading={loading} error={error}>
                <MasterTableContainer
                  columns={columns}
                  data={data}
                  totalRecords={totalRecords}
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  onPageChange={setPageIndex}
                  onPageSizeChange={setPageSize}
                  columnFilters={columnFilters}
                  setColumnFilters={setColumnFilters}
                  sorting={sorting}
                  onSortingChange={setSorting}
                  isBordered={false}
                  isPagination={true}
                  manualSorting={true}
                  colValue={setFilterValue}
                  onTableReady={setTableInstance}
                />
              </LoaderErrorWrapper>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </ListingPageContainer>
  );
};

export default Make;
